import i18next, { type i18n as I18nInstance, type Resource } from "i18next";
import { initReactI18next } from "react-i18next";
import type { Market } from "@ferrivia/api-contract";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, persistLocaleOverride, type Locale } from "./locale";

export interface CreateI18nOptions {
  resources: Resource;
  locale: Locale;
  /** Defaults to the shared product default ("fi"); pass the app's own default explicitly. */
  fallbackLng?: Locale;
  /** Deployment market. "us" forces English and applies `overlays` on top of `resources`. */
  market?: Market;
  overlays?: Resource;
}

const markets = new WeakMap<I18nInstance, Market>();

export function getMarket(instance: I18nInstance): Market {
  return markets.get(instance) ?? "fi";
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function deepMerge(base: Record<string, unknown>, overlay: Record<string, unknown>): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(overlay)) {
    const existing = merged[key];
    merged[key] = isPlainObject(existing) && isPlainObject(value) ? deepMerge(existing, value) : value;
  }
  return merged;
}

/** Deep-merges `overlay` over `base` per language and namespace; overlay leaves win. */
export function overlayResources(base: Resource, overlay: Resource): Resource {
  return deepMerge(base as Record<string, unknown>, overlay as Record<string, unknown>) as Resource;
}

export function createI18n({
  resources,
  locale,
  fallbackLng = DEFAULT_LOCALE,
  market = "fi",
  overlays,
}: CreateI18nOptions): I18nInstance {
  const instance = i18next.createInstance();
  markets.set(instance, market);
  const isUs = market === "us";
  void instance.use(initReactI18next).init({
    resources: isUs && overlays ? overlayResources(resources, overlays) : resources,
    lng: isUs ? "en" : locale,
    fallbackLng: isUs ? "en" : fallbackLng,
    supportedLngs: SUPPORTED_LOCALES,
    interpolation: { escapeValue: false },
    returnNull: false,
  });
  return instance;
}

export function mergeResources(...resourceSets: Resource[]): Resource {
  const merged: Resource = {};
  for (const set of resourceSets) {
    for (const [lng, namespaces] of Object.entries(set)) {
      merged[lng] = { ...(merged[lng] ?? {}), ...namespaces };
    }
  }
  return merged;
}

/** Explicit locale switch (e.g. a future switcher UI) — persists the override, unlike `useSyncUserLocale`. */
export function setLocale(i18n: I18nInstance, locale: Locale): void {
  persistLocaleOverride(locale);
  void i18n.changeLanguage(locale);
}
